import { useState, useEffect, useCallback } from 'react';
import { Sparkles, ChevronDown, ChevronUp, RefreshCw } from 'lucide-react';
import {
  createEvolutionRun,
  listEvolutionRuns,
  listEvolutionRounds,
  getEvolutionRun,
  type EvolutionRun,
  type EvolutionRound,
} from '../api/evolution';
import { streamJobEvents } from '../api/sse';
import ConfigDiffViewer from './ConfigDiffViewer';
import { useToast } from './ui/toastContext';

const getApiErrorMessage = (e: unknown): string => {
  const err = e as { response?: { data?: { detail?: unknown } }; message?: unknown };
  const detail = err.response?.data?.detail;
  if (typeof detail === 'string' && detail) return detail;
  if (typeof err.message === 'string' && err.message) return err.message;
  return '未知错误';
};

const STATUS_LABELS: Record<string, { text: string; color: string }> = {
  running: { text: '进行中', color: 'var(--color-primary, #58a6ff)' },
  completed: { text: '已完成', color: 'var(--color-success)' },
  failed: { text: '失败', color: 'var(--color-error)' },
};

const DECISION_LABELS: Record<string, { text: string; color: string }> = {
  accepted: { text: '已接受', color: 'var(--color-success)' },
  rolled_back: { text: '已回滚', color: 'var(--color-error)' },
};

const pct = (v: number | null | undefined): string => (v === null || v === undefined ? '—' : `${(v * 100).toFixed(1)}%`);

/** 分数曲线：各轮 before/after 综合分折线（纯 SVG，无图表库依赖）。 */
function ScoreCurve({ rounds }: { rounds: EvolutionRound[] }) {
  const points: { x: number; before: number; after: number | null }[] = [];
  rounds.forEach((r, i) => {
    if (r.score_before !== null && r.score_before !== undefined) {
      points.push({ x: i, before: r.score_before, after: r.score_after ?? null });
    }
  });
  if (points.length === 0) return null;
  const W = 560;
  const H = 160;
  const pad = 28;
  const stepX = points.length > 1 ? (W - pad * 2) / (points.length - 1) : 0;
  const sx = (i: number) => (points.length > 1 ? pad + i * stepX : W / 2);
  const sy = (v: number) => H - pad - v * (H - pad * 2);
  const beforePath = points.map((p, i) => `${i === 0 ? 'M' : 'L'}${sx(i)},${sy(p.before)}`).join(' ');
  const afterPoints = points.filter((p) => p.after !== null);
  const afterPath = afterPoints
    .map((p) => `${p === afterPoints[0] ? 'M' : 'L'}${sx(p.x)},${sy(p.after as number)}`)
    .join(' ');
  return (
    <svg width="100%" viewBox={`0 0 ${W} ${H}`} style={{ maxWidth: W, display: 'block' }} aria-label="各轮综合分曲线">
      {[0, 0.5, 1].map((v) => (
        <g key={v}>
          <line x1={pad} y1={sy(v)} x2={W - pad} y2={sy(v)} stroke="var(--border-color, #30363d)" strokeDasharray="4" />
          <text x={4} y={sy(v) + 4} fontSize={10} fill="var(--text-secondary, #8b949e)">
            {(v * 100).toFixed(0)}%
          </text>
        </g>
      ))}
      <path d={beforePath} fill="none" stroke="var(--color-primary, #58a6ff)" strokeWidth={2} />
      {afterPath && <path d={afterPath} fill="none" stroke="var(--color-success)" strokeWidth={2} strokeDasharray="6 3" />}
      {points.map((p, i) => (
        <g key={i}>
          <circle cx={sx(i)} cy={sy(p.before)} r={4} fill="var(--color-primary, #58a6ff)">
            <title>{`第 ${i + 1} 轮 before ${pct(p.before)}`}</title>
          </circle>
          {p.after !== null && (
            <circle cx={sx(i)} cy={sy(p.after)} r={4} fill="var(--color-success)">
              <title>{`第 ${i + 1} 轮 after ${pct(p.after)}`}</title>
            </circle>
          )}
          <text x={sx(i) - 8} y={H - 6} fontSize={10} fill="var(--text-secondary, #8b949e)">
            第{i + 1}轮
          </text>
        </g>
      ))}
      <text x={pad} y={14} fontSize={11} fill="var(--color-primary, #58a6ff)">
        ● before
      </text>
      <text x={pad + 80} y={14} fontSize={11} fill="var(--color-success)">
        ● after（候选）
      </text>
    </svg>
  );
}

/** 单轮记录：决策徽标 + 原因 + 改动 diff（复用 ConfigDiffViewer）。 */
function RoundCard({ round }: { round: EvolutionRound }) {
  const [expanded, setExpanded] = useState(false);
  const decision = DECISION_LABELS[round.decision] || { text: round.decision, color: 'var(--text-secondary)' };
  return (
    <div className="card" style={{ background: 'var(--bg-primary)', marginBottom: 8 }}>
      <div
        className="flex items-center justify-between"
        style={{ cursor: 'pointer' }}
        onClick={() => setExpanded((v) => !v)}
      >
        <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
          <strong>第 {round.round_no} 轮</strong>
          <span
            style={{
              fontSize: 12,
              padding: '2px 8px',
              borderRadius: 10,
              border: `1px solid ${decision.color}`,
              color: decision.color,
            }}
          >
            {decision.text}
          </span>
          <span style={{ fontSize: 13, color: 'var(--text-secondary, #8b949e)' }}>
            {pct(round.score_before)} → {pct(round.score_after)}
          </span>
        </div>
        {expanded ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
      </div>
      <div style={{ fontSize: 13, marginTop: 6 }}>{round.reason}</div>
      {expanded && (
        <div className="mt-2">
          {round.diff && round.diff.diff ? (
            <div>
              <h5 style={{ margin: '8px 0 4px' }}>改动 diff（白名单字段）</h5>
              <ConfigDiffViewer
                left={(round.diff.before as object) ?? {}}
                right={(round.diff.after as object) ?? {}}
              />
            </div>
          ) : (
            <div style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>无行级差异（候选未应用或未产出）</div>
          )}
          {round.candidate && (
            <details className="mt-2">
              <summary style={{ fontSize: 12, cursor: 'pointer' }}>候选改动原文（JSON）</summary>
              <pre style={{ fontSize: 12, overflow: 'auto' }}>{JSON.stringify(round.candidate, null, 2)}</pre>
            </details>
          )}
          {round.created_profile_id && (
            <div style={{ fontSize: 12, marginTop: 6, color: 'var(--color-success)' }}>
              新版本 Profile 已激活：{round.created_profile_id}
            </div>
          )}
        </div>
      )}
    </div>
  );
}

/** B23 模型切换告警条：黄色警示（沿用 theme.css --color-warning 变量与既有 rgba 底）。 */
function ModelDriftBanner({ message }: { message: string }) {
  return (
    <div
      style={{
        marginTop: 8,
        fontSize: 12,
        padding: '8px 10px',
        borderRadius: 6,
        border: '1px solid var(--color-warning)',
        background: 'rgba(240, 136, 62, 0.1)',
        color: 'var(--color-warning)',
      }}
    >
      ⚠ {message}
    </div>
  );
}

/** 自我进化面板（P12）：触发进化运行 + 分数曲线 + 每轮 diff 与决策徽标。 */
export default function EvolutionPanel({ agentId }: { agentId: string }) {
  const toast = useToast();
  const [runs, setRuns] = useState<EvolutionRun[]>([]);
  const [loading, setLoading] = useState(true);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [rounds, setRounds] = useState<EvolutionRound[]>([]);
  const [suiteId, setSuiteId] = useState('');
  const [maxRounds, setMaxRounds] = useState(1);
  const [starting, setStarting] = useState(false);

  const load = useCallback(
    async (keepSelection = true) => {
      try {
        const list = await listEvolutionRuns(agentId);
        setRuns(list);
        if (!keepSelection || !selectedId) {
          setSelectedId(list[0]?.id ?? null);
        }
      } catch (e) {
        toast.error('加载进化运行失败: ' + getApiErrorMessage(e));
      } finally {
        setLoading(false);
      }
    },
    [agentId, selectedId, toast],
  );

  const loadRounds = useCallback(
    async (runId: string | null) => {
      if (!runId) {
        setRounds([]);
        return;
      }
      try {
        setRounds(await listEvolutionRounds(runId));
      } catch (e) {
        toast.error('加载轮次记录失败: ' + getApiErrorMessage(e));
      }
    },
    [toast],
  );

  useEffect(() => {
    // 通过本地包装触发异步加载，避免在 effect 体内直接同步调用 setState（react-hooks/set-state-in-effect）
    const tick = () => {
      void load();
    };
    tick();
  }, [agentId]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const tick = () => {
      void loadRounds(selectedId);
    };
    tick();
  }, [selectedId, loadRounds]);

  // 进行中运行：订阅 job 事件流（P11 双通道：历史重建 + 实时 SSE）。事件驱动刷新
  // 详情与轮次，done 终态帧后流自然结束（运行转终态后本 effect 自动退订），不再轮询。
  const activeJobId = runs.find((r) => r.id === selectedId && r.status === 'running')?.job_id ?? null;
  useEffect(() => {
    if (!activeJobId || !selectedId) return;
    const jobId = activeJobId;
    const runId = selectedId;
    const controller = new AbortController();
    const refresh = async () => {
      try {
        const detail = await getEvolutionRun(runId);
        setRuns((prev) => prev.map((r) => (r.id === detail.id ? detail : r)));
        await loadRounds(detail.id);
      } catch {
        /* 刷新失败静默，等待下一事件或手动刷新 */
      }
    };
    const tick = () => {
      void streamJobEvents(jobId, () => void refresh(), controller.signal).catch(() => {
        /* 事件流中断静默降级：状态仍可经手动刷新获取 */
      });
    };
    tick();
    return () => controller.abort();
  }, [activeJobId, selectedId, loadRounds]);

  const handleStart = async () => {
    if (!suiteId.trim()) {
      toast.error('请填写评测套件 ID');
      return;
    }
    setStarting(true);
    try {
      const { run } = await createEvolutionRun(agentId, { suite_id: suiteId.trim(), max_rounds: maxRounds });
      toast.success('进化运行已启动');
      setSelectedId(run.id);
      await load();
      setSelectedId(run.id);
    } catch (e) {
      toast.error('启动进化失败: ' + getApiErrorMessage(e));
    } finally {
      setStarting(false);
    }
  };

  const selected = runs.find((r) => r.id === selectedId) || null;
  const selectedStatus = selected ? STATUS_LABELS[selected.status] : null;

  return (
    <div>
      <h3 className="mb-3">自我进化</h3>
      <p style={{ fontSize: 13, color: 'var(--text-secondary, #8b949e)' }}>
        评测驱动的一键进化：LLM 基于失败案例提出有界改动（人设 / 技能 / 工作流描述），自动跑分，
        <strong>综合分严格提升才激活新版本，否则自动回滚</strong>。每轮改动与决策全程可审计。
      </p>

      {/* 触发表单 */}
      <div className="card flex items-end gap-3" style={{ background: 'var(--bg-primary)', flexWrap: 'wrap' }}>
        <div style={{ flex: 2, minWidth: 220 }}>
          <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>评测套件 ID（内置套件）</label>
          <input
            value={suiteId}
            onChange={(e) => setSuiteId(e.target.value)}
            placeholder="如 sop-qa-suite"
            style={{ width: '100%' }}
          />
        </div>
        <div style={{ width: 110 }}>
          <label style={{ fontSize: 12, display: 'block', marginBottom: 4 }}>轮次</label>
          <input
            type="number"
            min={1}
            max={10}
            value={maxRounds}
            onChange={(e) => setMaxRounds(Math.max(1, Math.min(10, Number(e.target.value) || 1)))}
            style={{ width: '100%' }}
          />
        </div>
        <button className="primary" onClick={handleStart} disabled={starting}>
          <Sparkles size={14} style={{ marginRight: 4, verticalAlign: -2 }} />
          {starting ? '启动中…' : '开始进化'}
        </button>
        <button onClick={() => load()} title="刷新">
          <RefreshCw size={14} />
        </button>
      </div>

      {loading ? (
        <div className="mt-4">加载中…</div>
      ) : runs.length === 0 ? (
        <div className="mt-4" style={{ color: 'var(--text-secondary, #8b949e)' }}>
          还没有进化运行。填写评测套件 ID 并点击「开始进化」。
        </div>
      ) : (
        <div className="flex gap-4 mt-4" style={{ alignItems: 'flex-start', flexWrap: 'wrap' }}>
          {/* 运行列表 */}
          <div style={{ flex: 1, minWidth: 260 }}>
            {runs.map((r) => {
              const st = STATUS_LABELS[r.status] || { text: r.status, color: 'var(--text-secondary)' };
              const active = r.id === selectedId;
              return (
                <div
                  key={r.id}
                  className="card"
                  style={{
                    background: 'var(--bg-primary)',
                    marginBottom: 8,
                    cursor: 'pointer',
                    border: active ? '1px solid var(--color-primary, #58a6ff)' : undefined,
                  }}
                  onClick={() => setSelectedId(r.id)}
                >
                  <div className="flex items-center justify-between">
                    <strong style={{ fontSize: 13 }}>{r.suite_id}</strong>
                    <span style={{ fontSize: 12, color: st.color }}>{st.text}</span>
                  </div>
                  <div style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)', marginTop: 4 }}>
                    {r.rounds}/{r.max_rounds} 轮 · 接受 {r.accepted_rounds} 轮 · {pct(r.baseline_score)} → {pct(r.final_score)}
                  </div>
                  {r.model_drift?.drifted && (
                    <div style={{ fontSize: 12, color: 'var(--color-warning)', marginTop: 4 }}>
                      ⚠ 模型已切换（产出模型：{r.llm_fingerprint}）
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {/* 选中运行详情 */}
          <div style={{ flex: 2, minWidth: 320 }}>
            {selected && selectedStatus && (
              <div className="card" style={{ background: 'var(--bg-primary)' }}>
                <div className="flex items-center gap-2" style={{ flexWrap: 'wrap' }}>
                  <strong>运行详情</strong>
                  <span style={{ fontSize: 12, color: selectedStatus.color }}>{selectedStatus.text}</span>
                  <span style={{ fontSize: 12, color: 'var(--text-secondary, #8b949e)' }}>
                    综合分 {pct(selected.baseline_score)} → {pct(selected.final_score)}
                  </span>
                </div>
                {selected.outcome && <div style={{ fontSize: 13, marginTop: 4 }}>{selected.outcome}</div>}
                {selected.model_drift?.drifted && selected.model_drift.message && (
                  <ModelDriftBanner message={selected.model_drift.message} />
                )}
                <div className="mt-3">
                  <ScoreCurve rounds={rounds} />
                </div>
                <div className="mt-3">
                  <h5 style={{ margin: '0 0 8px' }}>轮次记录</h5>
                  {rounds.length === 0 ? (
                    <div style={{ fontSize: 13, color: 'var(--text-secondary, #8b949e)' }}>暂无轮次记录</div>
                  ) : (
                    rounds.map((r) => <RoundCard key={r.id} round={r} />)
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
}
